import { pillLabelFor } from "@/components/tpr/BookingTypePill/BookingTypePill";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isMemberAnAdmin, isMemberAPracticeManager, isOwnerOrgBooking } from "@/core/auth";
import { formatBookingOwnerLabel } from "@/lib/bookingOwnerLabel";
import { getPaletteBySwatch } from "@/lib/paletteBySwatch";
import { isBookingRepeating } from "@/lib/recurrence";
import { membershipTypeForDisplay, type MembershipType } from "@/types/MembershipType";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router";
import System from "../../BookingTypeDot/System";
import type { Booking } from "@/types/Booking";
import type { Member } from "@/types/Member";

// Compact pill style for the calendar bubble. Semi-transparent white background reads cleanly
// against the bubble's palette gradient without locking us into a specific room swatch.
// Exported so the Change Room modal (RoomSelector) renders the exact same pills as the
// day/week calendar.
export const BUBBLE_PILL_CLASSES =
  "inline-block rounded-full bg-white/80 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-gray-800";

const formatClockTime = (hhmm: string): string => {
  if (!hhmm || hhmm.length < 4) return hhmm;
  return `${hhmm.slice(0, 2)}:${hhmm.slice(2, 4)}`;
};

type BubbleProps = {
  booking: Booking;
  member: Member | null;
  top: number;
  height: number;
  allowClick?: boolean;
  locationSlug?: string;
};

const Bubble = ({ booking, member, top, height, allowClick = false, locationSlug }: BubbleProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const palette = getPaletteBySwatch(booking.room?.swatch ?? "", {
    discoModeEnabled: member?.settings?.discoModeEnabled,
  });

  // A member's OWN bookings always render solid black with white text — on both the day and week
  // calendars, and in both disco and non-disco mode — so they can spot their bookings at a glance
  // regardless of the room's colour. Elevated users see their own bookings highlighted the same way.
  const isMyBooking = !!member && booking.memberId === member.id;
  // Org owners see their org members' bookings highlighted the same way as their own, so they can
  // spot the bookings they manage among all the location's bubbles.
  const isHighlighted = isMyBooking || isOwnerOrgBooking(member, booking);
  const gradientTop = isHighlighted ? "from-black" : palette?.gradientTop;
  const gradientBottom = isHighlighted ? "to-black" : palette?.gradientBottom;
  const textClass = isHighlighted ? "text-white" : palette?.text;

  const animationAllowed = member?.settings?.allowAnimation;

  const animation = animationAllowed
    ? {
        initial: { opacity: 0, scale: 0.9, skewX: 5 },
        animate: { opacity: 1, scale: 1, skewX: 0 },
      }
    : { initial: {}, animate: {} };

  const handleClick = () => {
    if (allowClick) {
      const isElevatedMember =
        isMemberAnAdmin(member) || isMemberAPracticeManager(member);
      const slug = locationSlug || booking.location?.slug;
      const returnUrl = encodeURIComponent(location.pathname + location.search);

      if (isElevatedMember && slug) {
        navigate(`/location/${slug}/practice-bookings/${booking.id}?returnUrl=${returnUrl}`);
      } else {
        navigate(`/view-booking/${booking.id}?returnUrl=${returnUrl}`);
      }
    }
  };

  const typeLabel = booking.type
    ? membershipTypeForDisplay(booking.type as MembershipType).long
    : "Booking";
  // Pill content uses the shared pillLabelFor — "Flex" for adhoc, otherwise the long label.
  const typePillLabel = pillLabelFor(booking.type as MembershipType | undefined);
  // Slot bookings are intrinsically weekly (R13) even when their persisted recurrence is
  // absent — `isBookingRepeating` captures that. Drives the R pill and the tooltip's
  // "Repeat: Yes/No" row.
  const isRepeat = isBookingRepeating(booking);
  const ownerLabel = formatBookingOwnerLabel(booking);
  const startLabel = formatClockTime(booking.startTime);
  const endLabel = formatClockTime(booking.endTime);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.div
            initial={animation.initial}
            animate={animation.animate}
            aria-label={`Booking at ${booking.startTime} titled ${booking.title}`}
            className={`absolute w-full z-30 left-0 bg-gradient-to-b ${gradientTop} ${gradientBottom} ${textClass} text-xs hover:cursor-pointer p-1 flex flex-col pointer-events-auto overflow-hidden`}
            style={{
              top: `${top}px`,
              height: `${height}px`,
            }}
            onClick={handleClick}
          >
            {/*
              Three vertical rows: pills, time, title. Each row is `truncate min-w-0` so
              long text ellipsises rather than spilling sideways; the bubble's outer
              `overflow-hidden` clips short bookings (e.g. a 30-min slot at 36px shows pills
              + time, with the title clipped). Time uses `text-xs` so a 60-min bubble still
              fits all three rows comfortably.
            */}
            <div
              className="flex justify-start items-center gap-1 shrink-0"
              aria-label="Booking type and repeat indicator"
            >
              {booking.type && (
                <span
                  className={BUBBLE_PILL_CLASSES}
                  title={typeLabel}
                  data-testid={`booking-bubble-type-pill-${booking.id}`}
                >
                  {typePillLabel}
                </span>
              )}
              {isRepeat && (
                <span
                  className={BUBBLE_PILL_CLASSES}
                  title="Repeat booking"
                  data-testid={`booking-bubble-repeat-pill-${booking.id}`}
                >
                  R
                </span>
              )}
              <System booking={booking} />
            </div>
            {/* Time and title share font size + line-height for visual harmony (text-base/leading-4).
                Short bookings clip the title via the bubble's outer overflow-hidden — pills + time
                stay visible, which is the priority order. */}
            <span
              className={`mt-1 text-base leading-4 ${textClass} truncate min-w-0 block`}
              data-testid={`booking-bubble-time-${booking.id}`}
            >
              {booking.startTime}
            </span>
            <span className="text-base leading-4 truncate min-w-0 block">{ownerLabel}</span>
          </motion.div>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" className="max-w-xs">
          <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs">
            <span className="text-muted-foreground">Start</span>
            <span className="font-semibold">{startLabel}</span>
            <span className="text-muted-foreground">End</span>
            <span className="font-semibold">{endLabel}</span>
            <span className="text-muted-foreground">Owner</span>
            <span className="font-semibold">{ownerLabel}</span>
            <span className="text-muted-foreground">Type</span>
            <span className="font-semibold">{typeLabel}</span>
            <span className="text-muted-foreground">Repeat</span>
            <span className="font-semibold">{isRepeat ? "Yes" : "No"}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default Bubble;
